import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

// Helper: dado um profile, tem acesso completo?
export function hasFullAccess(profile) {
  if (!profile) return false;
  if (profile.plan === 'pro' || profile.plan === 'teacher') return true;
  if (profile.trial_ends_at && new Date(profile.trial_ends_at) > new Date()) return true;
  return false;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // If profile doesn't exist yet, create it with trial (handles email confirmation flow)
    if (!data) {
      const { data: authUser } = await supabase.auth.getUser();
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);
      const newProfile = {
        id: userId,
        name: authUser?.user?.user_metadata?.name || '',
        email: authUser?.user?.email || '',
        plan: 'free',
        trial_ends_at: trialEnd.toISOString(),
        plan_granted_by: 'trial',
      };
      await supabase.from('profiles').insert(newProfile);
      setProfile(newProfile);
    } else {
      setProfile(data);
    }
    setLoading(false);
  };

  const signUp = async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    if (data.user) {
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);
      await supabase.from('profiles').insert({
        id: data.user.id,
        name,
        email,
        plan: 'free',
        trial_ends_at: trialEnd.toISOString(),
        plan_granted_by: 'trial',
      });
    }
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => { await supabase.auth.signOut(); };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  };

  const hasAccess = hasFullAccess(profile);

  return (
    <AuthContext.Provider value={{ user, profile, loading, signUp, signIn, signOut, resetPassword, fetchProfile, hasAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
